// Beta channel routing. A doc at `beta_whitelist/{storeId}` means the shop runs
// on the beta build, so send it to BETA_HOST. Ported from xtable-pos-v3
// (`modules/pos/plugins/isBeta.plugin.ts` + `shop.ts:fetchBetaWhiteLists`).
//
// POS only — owner-mobile has many shops and no single storeId. It's excluded
// twice over: by the path check below, and because it never runs
// `bootstrap.init()`, so `authStore.storeId` stays null there.

import { getDoc } from "firebase/firestore";
import { setCookie } from "~/utils/cookie";

const BETA_HOST = "app2b.niceloop.com";
const COOKIE_DOMAIN = ".niceloop.com";
const OWNER_MOBILE_PREFIX = "/v2-owner-report-mobile";

export default defineNuxtPlugin({
  name: "beta-redirect",
  setup() {
    if (!import.meta.client) return;

    const { hostname, pathname } = window.location;

    // Already on beta — nothing to decide.
    if (hostname === BETA_HOST) return;
    if (pathname.startsWith(OWNER_MOBILE_PREFIX)) return;

    const authStore = useAuthStore();

    // storeId lands at bootstrap step 2 (strategy.loadAuth), before Layer A/B
    // load menus and the jobchain — so a beta shop doesn't boot the whole POS
    // on the wrong domain first.
    //
    // `checked` (not a stop() call) is what makes this fire once: with
    // `immediate: true` the callback runs synchronously during watch(), before
    // `stop` is assigned, so calling stop() in there would throw on the TDZ.
    let checked = false;
    let stop: (() => void) | null = null;

    stop = watch(
      () => authStore.storeId,
      async (storeId) => {
        if (!storeId || checked) return;
        checked = true;
        stop?.();

        try {
          const snap = await getDoc(useFirestore().getBetaWhiteListRef(String(storeId)));
          if (!snap.exists()) return;

          if (hostname === "localhost") {
            console.log("[beta-redirect] whitelisted, would redirect to", BETA_HOST, { storeId });
            return;
          }

          // Read by the beta site, not by this app — localStorage can't cross
          // the subdomain hop.
          setCookie("niceloop", JSON.stringify({ isBeta: true, storeId }), 30, COOKIE_DOMAIN);
          window.location.href = `https://${BETA_HOST}`;
        } catch (err) {
          // Never block boot on this — a failed lookup just means "stay put".
          console.warn("[beta-redirect] whitelist lookup failed", err);
        }
      },
      { immediate: true },
    );

    // The immediate run may have already resolved before `stop` existed.
    if (checked) stop();
  },
});
